import { useState, useEffect, useRef } from 'react';
import { useFilter } from '@/contexts/FiltersContext.context';
import { useOperators } from '@/hooks/useOperators';
import { PLAN_TYPES, type PlanType } from '@/config/api';
import './MobileOperatorSelector.scss';

export interface MobileOperatorSelectorProps {
  name?: string;
  planType?: PlanType;
}

export function MobileOperatorSelector({
  name = 'operator-mobile',
  planType = PLAN_TYPES.MOBILE,
}: MobileOperatorSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { operators, loading } = useOperators(planType);

  const [contextValue, setContextValue] = useFilter(name);

  // Close on click-outside
  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleMouseDown);
      return () => document.removeEventListener('mousedown', handleMouseDown);
    }
  }, [isOpen]);

  // Close with Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  const currentValues = Array.isArray(contextValue) ? contextValue : [];

  const handleToggle = (slug: string) => {
    const newSelected = currentValues.includes(slug)
      ? currentValues.filter((v) => v !== slug)
      : [...currentValues, slug];

    setContextValue(newSelected);
  };

  return (
    <div className="mobile-operator-selector" ref={containerRef}>
      <button
        type="button"
        className="mobile-operator-selector__trigger rounded-full"
        onClick={() => {
          if (!isOpen && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            setDropUp(spaceBelow < 340);
          }
          setIsOpen(!isOpen);
        }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="mobile-operator-selector__trigger-content">
          <span className="mobile-operator-selector__placeholder">Opérateurs</span>
        </span>
        <svg
          width="17"
          height="10"
          viewBox="0 0 17 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={`mobile-operator-selector__arrow${isOpen ? ' mobile-operator-selector__arrow--open' : ''}`}
        >
          <path
            d="M7.55531 9.25031C8.02406 9.71906 8.78531 9.71906 9.25406 9.25031L16.4541 2.05031C16.9228 1.58156 16.9228 0.820313 16.4541 0.351562C15.9853 -0.117188 15.2241 -0.117188 14.7553 0.351562L8.40281 6.70406L2.05031 0.355312C1.58156 -0.113438 0.820313 -0.113438 0.351562 0.355312C-0.117188 0.824062 -0.117188 1.58531 0.351562 2.05406L7.55156 9.25406L7.55531 9.25031Z"
            fill="#262626"
          />
        </svg>
      </button>

      {isOpen && (
        <div className={`mobile-operator-selector__panel${dropUp ? ' mobile-operator-selector__panel--up' : ''}`}>
          {!loading &&
            operators.map((operator) => {
              const isChecked = currentValues.includes(operator.slug);
              return (
                <label
                  key={operator.slug}
                  className={`mobile-operator-selector__option${isChecked ? ' mobile-operator-selector__option--active' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleToggle(operator.slug)}
                    className="mobile-operator-selector__checkbox"
                  />
                  <span className={`mobile-operator-selector__option-name${isChecked ? ' mobile-operator-selector__option-name--active' : ''}`}>
                    {operator.name}
                  </span>
                </label>
              );
            })}
        </div>
      )}
    </div>
  );
}

export default MobileOperatorSelector;
